import { allRowsReadPath } from '@/platform/lists/lib/listSeedRequest';

const TABLE_ID_STORAGE_KEY = 'clinicalTrials:myStudies:tableId';

function storageKey(userKey: string): string {
  return `${TABLE_ID_STORAGE_KEY}:${userKey}`;
}

/** Storage can be missing or blocked; the cache only speeds up the next open. */
export function readMyStudiesTableId(userKey: string, storage?: Storage): string | null {
  try {
    const value = (storage ?? window.localStorage).getItem(storageKey(userKey));
    return typeof value === 'string' && value ? value : null;
  } catch {
    return null;
  }
}

export function writeMyStudiesTableId(userKey: string, tableId: string | null): void {
  try {
    if (tableId) window.localStorage.setItem(storageKey(userKey), tableId);
    else window.localStorage.removeItem(storageKey(userKey));
  } catch {
    // The next open falls back to the ordinary lookup.
  }
}

const GROUPING_STORAGE_KEY = 'clinicalTrials:myStudies:grouping';

/**
 * How the List was last grouped. Grouped by chat it reads every row at once,
 * so the next open can start that read beside the opening request.
 */
export type MyStudiesGrouping = 'chat' | 'status';

export function readMyStudiesGrouping(
  userKey: string,
  storage?: Storage
): MyStudiesGrouping | null {
  try {
    const value = (storage ?? window.localStorage).getItem(`${GROUPING_STORAGE_KEY}:${userKey}`);
    return value === 'chat' || value === 'status' ? value : null;
  } catch {
    return null;
  }
}

/**
 * The read of every row the List sends when it opens grouped by chat, from
 * what the last open stored; null when that open grouped by status.
 */
export function myStudiesRowsRead(userKey: string, storage?: Storage): string | null {
  const tableId = readMyStudiesTableId(userKey, storage);
  return tableId && readMyStudiesGrouping(userKey, storage) === 'chat'
    ? allRowsReadPath(tableId)
    : null;
}

/** The key the List's cache is stored under: the user in their tenant. */
export function myStudiesUserKey(tenantId: string | null | undefined, userId: unknown): string {
  return `${tenantId ?? 'no-tenant'}:${userId ?? 'anonymous'}`;
}

export function writeMyStudiesGrouping(userKey: string, grouping: MyStudiesGrouping): void {
  try {
    window.localStorage.setItem(`${GROUPING_STORAGE_KEY}:${userKey}`, grouping);
  } catch {
    // The next open reads its rows after the opening request, as before.
  }
}
